import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { UserPlus, Phone, Mail, Star, X, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import { toast } from 'sonner';

const API_URL = API_BASE;

export const Drivers = () => {const { t } = useTranslation("ezparcel");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDriver, setNewDriver] = useState({
    name: '',
    phone: '',
    email: '',
    licenseNumber: '',
    vehicleType: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('ezerpToken');
        const response = await fetch(`${API_URL}/api/ezparcel/drivers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setDrivers(data.map((d) => ({
            id: d.driver_id || d.id,
            name: d.name,
            phone: d.phone,
            email: d.email,
            status: d.status || 'available',
            rating: d.rating || 4.5,
            deliveries: d.total_deliveries || 0,
            avatar: d.avatar
          })));
        }
      } catch (err) {
        console.error('Failed to fetch drivers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDrivers();
  }, []);

  const handleAddDriver = async () => {
    if (!newDriver.name || !newDriver.phone || !newDriver.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ezparcel/drivers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
        },
        body: JSON.stringify({
          name: newDriver.name,
          phone: newDriver.phone,
          email: newDriver.email,
          license_number: newDriver.licenseNumber,
          vehicle_type: newDriver.vehicleType
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add driver');
      }

      const savedDriver = await response.json();

      // Convert API response to frontend format
      const driver = {
        id: savedDriver.id,
        name: savedDriver.name,
        phone: savedDriver.phone,
        email: savedDriver.email,
        status: savedDriver.status,
        rating: savedDriver.rating,
        completedDeliveries: savedDriver.completed_deliveries,
        onTimeRate: savedDriver.on_time_rate,
        currentRoute: null,
        vehicle: null,
        avatar: savedDriver.avatar
      };

      setDrivers([driver, ...drivers]);
      setNewDriver({ name: '', phone: '', email: '', licenseNumber: '', vehicleType: '' });
      setIsAddDialogOpen(false);
      toast.success(`Driver "${newDriver.name}" added successfully!`);
    } catch (error) {
      console.error('Error adding driver:', error);
      toast.error('Failed to add driver. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("drivers", "Drivers")}</h1>
          <p className="text-muted-foreground mt-2">{t("manageDriverProfiles", "Manage driver profiles, performance, and assignments")}

          </p>
        </div>
        <Button
          className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="add-driver-btn">
          
          <UserPlus className="mr-2 h-4 w-4" />{t("addDriver", "Add Driver")}

        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {drivers.map((driver) =>
        <Card key={driver.id} className="card-elevated">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={driver.avatar} />
                  <AvatarFallback>{driver.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-lg">{driver.name}</h3>
                      <p className="text-sm text-muted-foreground">{driver.id}</p>
                    </div>
                    <Badge
                    className={`status-badge ${
                    driver.status === 'active' ? 'status-completed' : 'bg-gray-100 text-gray-700'}`
                    }>
                    
                      {driver.status.toUpperCase()}
                    </Badge>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>{driver.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span>{driver.email}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">{t("rating", "Rating")}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-medium">{driver.rating}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t("deliveries", "Deliveries")}</p>
                        <p className="font-medium mt-1">{driver.completedDeliveries.toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">{t("ontimeRate", "On-Time Rate")}</span>
                        <span className="font-medium">{driver.onTimeRate}%</span>
                      </div>
                      <Progress value={driver.onTimeRate} className="h-2" />
                    </div>
                  </div>

                  {driver.currentRoute &&
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground">{t("currentRoute", "Current Route")}</p>
                      <p className="text-sm font-medium mt-1">{driver.currentRoute}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t("vehicle", "Vehicle:")}{driver.vehicle}</p>
                    </div>
                }

                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1">{t("viewProfile", "View Profile")}

                  </Button>
                    <Button variant="outline" size="sm" className="flex-1">{t("assignRoute", "Assign Route")}

                  </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Driver Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />{t("addNewDriver", "Add New Driver")}

            </DialogTitle>
            <DialogDescription>{t("enterTheDrivers", "Enter the driver's information to add them to the fleet.")}

            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="driver-name">{t("fullName", "Full Name *")}</Label>
              <Input
                id="driver-name"
                placeholder="John Smith"
                value={newDriver.name}
                onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} />
              
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="driver-phone">{t("phoneNumber", "Phone Number *")}</Label>
                <Input
                  id="driver-phone"
                  placeholder="+1 (555) 123-4567"
                  value={newDriver.phone}
                  onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} />
                
              </div>
              <div className="grid gap-2">
                <Label htmlFor="driver-email">{t("email", "Email *")}</Label>
                <Input
                  id="driver-email"
                  type="email"
                  placeholder="driver@ezparcel.com"
                  value={newDriver.email}
                  onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })} />
                
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="driver-license">{t("licenseNumber", "License Number")}</Label>
                <Input
                  id="driver-license"
                  placeholder="DL-123456789"
                  value={newDriver.licenseNumber}
                  onChange={(e) => setNewDriver({ ...newDriver, licenseNumber: e.target.value })} />
                
              </div>
              <div className="grid gap-2">
                <Label htmlFor="driver-vehicle-type">{t("preferredVehicleType", "Preferred Vehicle Type")}</Label>
                <Select
                  value={newDriver.vehicleType}
                  onValueChange={(value) => setNewDriver({ ...newDriver, vehicleType: value })}>
                  
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="van">{t("van", "Van")}</SelectItem>
                    <SelectItem value="truck">{t("truck", "Truck")}</SelectItem>
                    <SelectItem value="motorcycle">{t("motorcycle", "Motorcycle")}</SelectItem>
                    <SelectItem value="car">{t("car", "Car")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              onClick={handleAddDriver}
              disabled={isLoading}
              className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
              
              {isLoading ? 'Adding...' : 'Add Driver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};